import {
  ChevronDown,
  Copy,
  Database,
  Globe,
  Loader2,
  Plus,
  ShieldAlert,
  Sparkles,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AiButton } from '@/components/ui/ai-button'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { forkDefaultName } from '@/features/schema/fork'
import { representativeSchema } from '@/features/schema/format'
import type { SchemaGroup } from '@/features/schema/types'
import type { Schema } from '@/services/api/schemas'
import { SchemaMetaChips, VersionPill } from './SchemaHero'

interface CaseSchemaIntroProps {
  caseName?: string
  /** The workspace-default (global active) schema extraction uses until a case
   * schema exists, or null when no global default is set. */
  globalDefault: Schema | null
  /** Shared global schemas offered as starting points (newest version each). */
  globalGroups: SchemaGroup[]
  actions: {
    startFromGlobal: (schemaId: string, name: string) => Promise<void>
    createBlank: () => void
    createWithAgent: () => void
  }
}

/**
 * Case Schema page — empty state (SP-385). A case has no schema of its own yet,
 * so extraction follows the workspace default. This guides the user to give the
 * case one dedicated schema: primarily with the agent, or manually by copying a
 * global schema or starting from scratch. The copy is private to
 * this case, and the choice sticks — re-picking later (the editor's "change
 * starting point") deletes the schema it replaces — so the global path confirms
 * intent.
 */
export function CaseSchemaIntro({
  caseName,
  globalDefault,
  globalGroups,
  actions,
}: CaseSchemaIntroProps) {
  const [forkTarget, setForkTarget] = useState<Schema | null>(null)

  return (
    <div className="flex flex-col gap-6">
      <AgentStartingPoint actions={actions} />
      <DefaultSchemaBanner globalDefault={globalDefault} />
      <GlobalStartingPoints groups={globalGroups} onPick={setForkTarget} />

      <StartFromGlobalDialog
        target={forkTarget}
        caseName={caseName}
        onOpenChange={open => !open && setForkTarget(null)}
        onConfirm={actions.startFromGlobal}
      />
    </div>
  )
}

function AgentStartingPoint({ actions }: Pick<CaseSchemaIntroProps, 'actions'>) {
  const { t } = useTranslation()
  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card">
      <CardContent className="flex flex-col items-start gap-5 p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <div className="flex size-11 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
            <Sparkles className="size-5" />
          </div>
          <Badge variant="secondary">{t('schemaAgent.empty.recommended')}</Badge>
        </div>
        <div className="max-w-2xl space-y-2">
          <h2 className="text-xl font-semibold tracking-tight text-foreground">
            {t('schemaAgent.empty.title')}
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('schemaAgent.empty.description')}
          </p>
        </div>
        <div className="flex max-md:w-full max-md:flex-col flex-wrap items-center max-md:items-stretch gap-3">
          <AiButton emphasis="neon" size="lg" onClick={actions.createWithAgent}>
            {t('schemaAgent.empty.createWithAgent')}
          </AiButton>
          <Button variant="ghost" onClick={actions.createBlank}>
            <Plus className="size-4" />
            {t('schemaAgent.empty.createManually')}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t('schemaAgent.empty.steps')}</p>
      </CardContent>
    </Card>
  )
}

/** Keep templates available without competing with the primary agent flow. */
function GlobalStartingPoints({
  groups,
  onPick,
}: {
  groups: SchemaGroup[]
  onPick: (schema: Schema) => void
}) {
  const { t } = useTranslation()
  return (
    <Collapsible className="group/templates border-t border-border pt-4">
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto w-full justify-start gap-3 px-0 py-2 text-left whitespace-normal"
        >
          <Copy className="size-4 shrink-0 text-muted-foreground" />
          <span className="flex-1 space-y-1">
            <span className="block text-sm font-medium">{t('schemaAgent.empty.templates')}</span>
            <span className="block text-xs font-normal text-muted-foreground">
              {t('schemaAgent.empty.templatesHelp')}
            </span>
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/templates:rotate-180" />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4">
        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('schemaAgent.empty.noTemplates')}</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {groups.map(group => (
              <StartingPointCard
                key={group.name}
                group={group}
                onPick={() => onPick(representativeSchema(group))}
              />
            ))}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}

/** A quiet reminder of the extraction fallback, secondary to schema creation. */
function DefaultSchemaBanner({ globalDefault }: { globalDefault: Schema | null }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
      <Globe className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
      <div className="min-w-0">
        {globalDefault ? (
          <p className="text-sm text-muted-foreground">
            Extraction is using the workspace default —{' '}
            <span className="font-medium">
              {globalDefault.name} <span className="tabular-nums">v{globalDefault.version}</span>
            </span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">No workspace-default schema is set.</p>
        )}
        <p className="mt-0.5 text-xs text-muted-foreground">
          {globalDefault
            ? 'This case follows the default until you create its own schema.'
            : 'Create a schema for this case to define how its documents are extracted.'}
        </p>
      </div>
    </div>
  )
}

/** A global schema rendered as a selectable starting point. */
function StartingPointCard({ group, onPick }: { group: SchemaGroup; onPick: () => void }) {
  const schema = representativeSchema(group)
  return (
    <Card className="bg-card/40 transition-colors hover:border-primary/40">
      <CardContent className="flex h-full flex-col gap-3 py-4">
        <div className="flex items-center gap-2 min-w-0">
          <Database className="h-4 w-4 text-primary shrink-0" />
          <span className="font-medium text-foreground truncate max-md:line-clamp-2 max-md:whitespace-normal max-md:break-words">
            {group.name}
          </span>
          <span className="ml-auto shrink-0">
            <VersionPill schema={schema} />
          </span>
        </div>
        {schema.description && (
          <p className="text-xs text-muted-foreground line-clamp-2 min-h-8">{schema.description}</p>
        )}
        <SchemaMetaChips schema={schema} />
        <Button variant="outline" size="sm" onClick={onPick} className="mt-auto border-border">
          <Copy className="h-3.5 w-3.5 mr-1.5" />
          Use as starting point
        </Button>
      </CardContent>
    </Card>
  )
}

/** One-way-door confirm: forking a global schema makes a private copy this case
 * will use exclusively. Collects the editable copy name. */
function StartFromGlobalDialog({
  target,
  caseName,
  onOpenChange,
  onConfirm,
}: {
  target: Schema | null
  caseName?: string
  onOpenChange: (open: boolean) => void
  onConfirm: (schemaId: string, name: string) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  // Reseed the editable name each time a new starting point is picked.
  useEffect(() => {
    if (target) setName(forkDefaultName(caseName, target.name))
  }, [target, caseName])

  const submit = async () => {
    const trimmed = name.trim()
    if (!target || !trimmed || busy) return
    setBusy(true)
    try {
      await onConfirm(target.id, trimmed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={target !== null} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Create this case's schema</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            This copies <span className="font-medium text-foreground">{target?.name}</span> into a
            private schema for this case. From then on the case uses only its own schema.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-start gap-2 rounded-md border border-warning-border bg-warning-surface p-3">
          <ShieldAlert className="h-4 w-4 text-warning-foreground shrink-0 mt-0.5" />
          <p className="text-xs text-warning-foreground">
            The copy starts at v1; later edits create new versions. You can change the starting
            point afterwards, but that deletes this schema and its versions.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="start-from-global-name" className="text-foreground">
            Name
          </Label>
          <Input
            id="start-from-global-name"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') submit()
            }}
            className="bg-muted border-border"
            // biome-ignore lint/a11y/noAutofocus: name is the sole field of an intentional prompt
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-border">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !name.trim()}>
            {busy ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Copy className="h-4 w-4 mr-1" />
            )}
            Create case schema
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
